import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { lbl } from "@/lib/terms";
import { api, mediaUrl } from "@/lib/api";
import { buildBreadcrumbJsonLd } from "@/lib/seo";

export default function AgencyGuideDetail() {
  const { slug: urlSlug, guideSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const isUs = market === "us";
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setArticle(null);
    setError(false);
    // Hold until the tenant is known: on a custom domain the slug arrives
    // with the brand. See useTenantSlug.
    if (!tenantKnown) return;
    api.get(`/crm/public/guides/${guideSlug}?tenant=${tenantSlug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setError(true));
  }, [tenantSlug, tenantKnown, guideSlug]);

  const guidesHref = tenantPath("guides");
  const valuationHref = tenantPath("valuation");

  const articleJsonLd = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    url: window.location.href,
    datePublished: article.created_at,
    dateModified: article.updated_at,
    author: biz.name ? { "@type": "Organization", name: biz.name } : undefined,
    publisher: biz.name ? { "@type": "Organization", name: biz.name } : undefined,
  } : null;

  const breadcrumbs = article ? buildBreadcrumbJsonLd([
    { name: "Home", url: window.location.href.replace(/\/guides\/.*$/, "") || `${window.location.origin}/` },
    { name: "Guides", url: guidesHref },
    { name: article.title, url: window.location.href.split("?")[0] },
  ]) : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={article ? `${article.title} | ${biz.name || "Guide"}` : undefined}
        description={article?.excerpt || article?.body?.slice(0, 200)}
        image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
        jsonLd={[articleJsonLd, breadcrumbs]}
        noindex={!article}
      />
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12">
          <Link to={guidesHref} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />Back to guides
          </Link>

          {error ? (
            <div className="mt-16 text-center text-muted-foreground">
              This guide isn't available. <Link to={guidesHref} className="text-primary underline">Browse all guides</Link>.
            </div>
          ) : !article ? (
            <div className="mt-16 text-center text-muted-foreground">Loading…</div>
          ) : (
            <>
              {article.category && (
                <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mt-10">{article.category}</div>
              )}
              <h1 className="font-serif-display text-4xl sm:text-5xl text-primary mt-3 leading-tight">{article.title}</h1>
              <div className="mt-10 space-y-6 text-primary/90 leading-relaxed">
                {article.body.split("\n\n").filter((p) => p.trim()).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-14 border-t border-border pt-10 text-center">
                <h2 className="font-serif-display text-2xl text-primary">Thinking of moving?</h2>
                <p className="text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
                  {isUs
                    ? "Request a free home valuation from a local agent. No obligation."
                    : "Get a free, 60-second valuation of your home using HM Land Registry data."}
                </p>
                <Link to={valuationHref}>
                  <Button size="lg" className="mt-6 rounded-sm">
                    <Sparkles className="w-4 h-4 mr-2" />{lbl("free_valuation", market)} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
